// eslint-disable @typescript-eslint/no-explicit-any

import { useCallback } from 'react';
import Datasheet from '@scalartech/react-datasheet';
import { isEmpty } from 'lodash';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import ScalarDatasheetProps, { AttributesRendererResponseType, Selection } from './ScalarDatasheet.props';
import useDatasheetClipboardPaste from './useDatasheetClipboardPaste';
import { EventType, useEventBus } from '../../context';
import { ValueViewer } from '../Spreadsheet/components';

const ScalarDatasheet = ({ valueViewer = ValueViewer, valueRenderer, ...props }: ScalarDatasheetProps) => {
  const { clearSelectedFlag, setClearSelected, setSelectedCell, selectedCell, isColumnDragging } = useFeatureSpreadsheet();
  const { data, virtualization } = props;
  const renderValue = valueRenderer || ((cell: ICell) => cell.value);
  const { publish } = useEventBus();

  const { selected, setSelected, setMaxAllowedCells, containerRef, handleOnPaste } = useDatasheetClipboardPaste({
    data,
    onCellsChanged: props.onCellsChanged,
    clearSelectedFlag,
    setClearSelected,
  });

  const attributesRenderer = useCallback(
    (cell: ICell): AttributesRendererResponseType => ({
      ...(cell.key ? { id: `CELL-${cell.key}` } : {}),
      'data-parent': cell.parent ?? '',
    }),
    []
  );

  const handleOnSelect = useCallback(
    (selection: Selection | null) => {
      if (isColumnDragging) {
        return;
      }
      if (selection) {
        const { start, end } = selection;
        const maxAllowedRows = end.i - start.i + 1;
        const maxAllowedColumns = end.j - start.j + 1;

        setMaxAllowedCells({
          maxAllowedRows,
          maxAllowedColumns,
        });
        const isTotalColumn = data[1]?.some(({ isTotal }) => isTotal);
        if (setSelectedCell && !isTotalColumn && !isEmpty(start)) {
          const cellInfo: ICell = data?.[start.i]?.[start.j];
          const { insideLedger, allowInsideLedger } = cellInfo;
          if (!insideLedger || allowInsideLedger) {
            // Capture the end cell so the RowIndicator can compute
            // the indicator height for multi-row selections
            const endCellInfo: ICell | undefined = data?.[end.i]?.[end.j];
            const selectedCellData = {
              ...selection,
              rowNumber: start.i,
              cellInfo,
              ...(endCellInfo ? { endCellInfo } : {}),
            };
            publish(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, [selectedCellData, selectedCell]);
            setSelectedCell(selectedCellData);
          }
        }
      }
      setSelected(selection);
    },
    [data, setSelectedCell, publish, selectedCell, isColumnDragging, setMaxAllowedCells, setSelected]
  );

  return (
    <div ref={containerRef}>
      <Datasheet
        parsePaste={handleOnPaste}
        onSelect={handleOnSelect}
        selected={selected}
        // any is used because the library does not have typing correctly configured
        // when migrating from js->ts the props incoming to this component are typed
        // but the library has its own typing it expects
        valueRenderer={renderValue as any}
        virtualization={virtualization}
        valueViewer={valueViewer as any}
        attributesRenderer={props.attributesRenderer ?? (attributesRenderer as any)}
        cellRenderer={props.cellRenderer as any}
        dataEditor={props.dataEditor as any}
        onCellsChanged={props.onCellsChanged as any}
        rowRenderer={props.rowRenderer as any}
        data={props.data as any}
        className={props.className as any}
        dataRenderer={props.dataRenderer as any}
        isCellNavigable={props.isCellNavigable as any}
      />
    </div>
  );
};

export default ScalarDatasheet;
